import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, ElementRef, inject, signal, ViewChild, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter, Input, SimpleChanges, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { FocusManagementService } from '../../services/focus-management.service';
import { SseService } from '../../services/sse.service';
import { ButtonModule } from 'primeng/button';
import { CourseFacade } from '../../../features/trainer/services/course.facade';
import { MessageService } from 'primeng/api';
import { Notification } from '../../../features/learner/models/course.model';
import { ToastModule } from "primeng/toast";
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserwaySettingsService } from '../../services/userway-settings.service';
import { take } from 'rxjs';
import { SkeletonModule } from "primeng/skeleton";

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  page_size: number;
}

@Component({
  selector: 'app-notifications-menu',
  standalone: true,
  imports: [CommonModule, ButtonModule, ToastModule, ToggleSwitchModule, FormsModule, SkeletonModule],
  templateUrl: './notifications-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsMenuComponent implements OnInit,OnChanges, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('notificationSound') notificationSound!: ElementRef<HTMLAudioElement>;
  @ViewChild('notificationsMenu') notificationsMenu!: ElementRef;
  @Output() closeNotifications = new EventEmitter<void>();
  @Input() showNotifications: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  private reqId = 0;

  showUnread = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  isLoadingMore = signal<boolean>(false);
  hasLoadedAll = signal<boolean>(false);
  isProcessingNotification = signal<boolean>(false);
  isSoundMuted = signal<boolean>(false);
  isMenuOpened = signal<boolean>(false);
  notifications = signal<Notification[]>([]);
  private isTogglingFilter = false;  // Prevent sound during filter toggle

  // Output event - emitted when a notification is clicked to close the notification popover
  @Output() notificationClicked = new EventEmitter<void>();

  private scrollToTopTimeOut: ReturnType<typeof setTimeout> | null = null;
  private currentPage = 1;
  private readonly pageSize = 10;
  private lastScrollCheck = 0;
  private readonly scrollThrottleMs = 300;

  todayNotifications = computed(() => {
    const bucket = this.dateBoundaries();
    return this.notifications().filter(n => n.__ts >= bucket.todayStart);
  });

  yesterdayNotifications = computed(() => {
    const bucket = this.dateBoundaries();
    return this.notifications().filter(n => 
      n.__ts >= bucket.yesterdayStart && n.__ts < bucket.todayStart
    );
  });

  olderNotifications = computed(() => {
    const bucket = this.dateBoundaries();
    return this.notifications().filter(n => n.__ts < bucket.yesterdayStart);
  });

  private readonly dateBoundaries = computed(() => {
    // Get user's local timezone boundaries
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
    return { todayStart, yesterdayStart };
  });

  groupedNotifications = computed(() => {
    const today = this.todayNotifications();
    const yesterday = this.yesterdayNotifications();
    const older = this.olderNotifications();
    const groups = [];

    if (today.length > 0) {
      groups.push({ label: 'TODAY', items: today });
    }
    if (yesterday.length > 0) {
      groups.push({ label: 'YESTERDAY', items: yesterday });
    }
    if (older.length > 0) {
      groups.push({ label: 'OLDER', items: older });
    }
    return groups;
  });

  // Memoized unread count
  unreadCount = computed(() => {
    return this.notifications().filter(n => !n.is_read).length;
  });


  constructor(
    private readonly focusManagementService: FocusManagementService,
    private readonly sseService: SseService,
    private readonly courseFacade: CourseFacade,
    private readonly messageService: MessageService,
    private readonly router: Router,
    public readonly userwaySettings: UserwaySettingsService,
  ) {
    this.subscribeNotificationUpdates();
    this.subscribeNotificationReconnect();
  }

  ngOnInit() {
    globalThis.window.addEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    const menu = this.notificationsMenu?.nativeElement;

    const targetEl = event.target as HTMLElement;

    const clickedOnMenu = menu?.contains(targetEl);
    if (clickedOnMenu) return;
    this.closeNotifications.emit();
  };

  toggleMenu() {
    this.isMenuOpened.set(!this.isMenuOpened());
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes?.['showNotifications']?.currentValue){
      // Reset pagination state when menu opens to always start from page 1
      this.currentPage = 1;
      this.hasLoadedAll.set(false);
      this.notifications.set([]);
      this.lastScrollCheck = 0;
      this.getNotificationsList(true);
      this.focusManagementService.focusNewElement('#notifications-heading', 'Focused moved to notifications list');
    }
  }
  
  subscribeNotificationUpdates() {
    effect(() => {
      let incomingNotification = this.sseService.notificationObjectsSignal();
      if (!incomingNotification) return;

      // Ensure the notification has required fields with correct defaults
      incomingNotification = {
        ...incomingNotification,
        is_read: incomingNotification?.is_read ? incomingNotification.is_read : false,
        created_ts: incomingNotification.created_ts || new Date().toISOString()
      };

      // Avoid duplicate if notification is already at the top
      if (this.notifications().length == 0 || (this.notifications().length > 0 && this.notifications()[0].notification_id === incomingNotification.notification_id)) {
        return;
      }

      incomingNotification = this.normalizeNotificationDate(incomingNotification);

      // Always prepend new notification (will show on page 1)
      // If user is on page 2+, notification will be prepended but they won't see it
      // until they scroll back to top or refresh
      this.notifications.update(list => [incomingNotification, ...list]);

      // Play notification sound
      this.playNotificationSound();
    }, { allowSignalWrites: true });
  }

  subscribeNotificationReconnect() {
    effect(() => {
      if (this.sseService.eventSourceReconnect()) {
        this.getNotificationsList(true);  // Reload from first page after reconnect
      }
    });
  }

  trackByNotificationId(_index: number, notification: Notification): string {
    return notification.notification_id;
  }

  trackByGroup(_index: number, group: { label: string; items: Notification[] }): string {
    return group.label;
  }

  onToggleChange(value: boolean) {
    // Reset pagination when toggling between all/unread
    this.isTogglingFilter = true;  // Prevent sound during filter toggle
    this.showUnread.set(value);
    this.currentPage = 1;
    this.hasLoadedAll.set(false);
    this.lastScrollCheck = 0;  // Reset throttle timer
    this.notifications.set([]);
    this.getNotificationsList(true);  // true = reset, load first page
  }

  getNotificationsList(isReset: boolean = false) {
    const isInitialLoad = isReset || this.notifications().length === 0;
    const loadingSignal = isInitialLoad ? this.isLoading : this.isLoadingMore;
    const myId = ++this.reqId;

    loadingSignal.set(true);

    let params: { page: number; page_size: number; is_read?: boolean } = {
      page: this.currentPage,
      page_size: this.pageSize
    };
    params = this.showUnread() ? { ...params, is_read: false } : params;

    this.courseFacade.getNotificationsList(params)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          loadingSignal.set(false);
          if (myId !== this.reqId) return;

          if (res.success) {
            const newNotifications = res?.result?.data.map(this.normalizeNotificationDate) || [];
            const pagination = res.result?.pagination;

            // Save scroll position before updating notifications
            const scrollEl = this.scrollContainer?.nativeElement;
            const previousScrollTop = scrollEl ? scrollEl.scrollTop : 0;

            if (isReset) {
              // First page - replace all
              this.notifications.set(newNotifications);
              this.isTogglingFilter = false;  // Clear flag after reset load
            } else {
              // Lazy load - append to existing
              this.notifications.update(current => [...current, ...newNotifications]);
            }

            // Check if we've loaded all notifications
            if (pagination && pagination.current_page >= pagination.total_pages) {
              this.hasLoadedAll.set(true);
            } else {
              this.currentPage++;
            }

            if (isReset) {
              this.scrollToTop();
            } else if (scrollEl) {
              // Restore scroll position after lazy load append to prevent jumping to top
              setTimeout(() => {
                scrollEl.scrollTop = previousScrollTop;
              }, 0);
            }
          } else {
            if (isReset) {
              this.notifications.set([]);
              this.isTogglingFilter = false;  // Clear flag on warning too
            }
            this.messageService.clear();
            this.messageService.add({
              severity: 'warn',
              summary: 'Warning',
              detail: res.message,
            });
          }
        },
        error: (err) => {
          loadingSignal.set(false);
          if (myId !== this.reqId) return;
          console.error('Error fetching notifications:', err);
          if (isReset) {
            this.notifications.set([]);
            this.isTogglingFilter = false;  // Clear flag on error too
          }
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch notifications. Please reload and try again!',
          });
        }
      });
  }

  /**
   * Detect scroll and load more notifications when user scrolls near bottom
   * Throttled to prevent excessive function calls
   */
  onScrollContainer(event: Event): void {
    // Skip if already loading or all loaded
    if (this.isLoadingMore() || this.hasLoadedAll()) {
      return;
    }

    const scrollEl = event.target as HTMLElement;
    const threshold = 200;  // Load more when 200px from bottom

    // Check if user has scrolled near the bottom
    const isNearBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < threshold;

    if (isNearBottom) {
      // Throttle API calls - only allow one every 300ms
      const now = Date.now();
      if (now - this.lastScrollCheck < this.scrollThrottleMs) {
        return;
      }
      this.lastScrollCheck = now;

      this.getNotificationsList(false);  // Load more (not a reset)
    }
  }

  scrollToTop() {
    this.scrollToTopTimeOut = setTimeout(() => {
      const container = this.scrollContainer;
      if (container) {
        container.nativeElement.scrollTop = 0;
      }
    }, 0);
  }

  normalizeNotificationDate(n: Notification) {
    return { ...n, __ts: new Date(n.created_ts).getTime() } as Notification & { __ts: number };
  }

  markNotificationAsRead(notification: Notification) {
    if (notification.is_read) return;
    const notificationId = notification.notification_id;
    this.courseFacade.markNotificationAsRead(notificationId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (res) => {
        this.sseService.notificationsUnreadCount.set(res?.result?.unread_count);
        this.notifications.update(currentList =>
          currentList.map(n =>
            n.notification_id === notificationId ? { ...n, is_read: true } : n
          )
        );
      },
      error: (err) => {
        console.error('Error marking notification read:', err);
      }
    });
  }

  markAllAsRead() {
    this.courseFacade.markAllAsRead()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (res: any) => {
        if(res.success) {
          this.isMenuOpened.set(false);
          this.sseService.notificationsUnreadCount.set(0);
          this.notifications.update(list =>
            list.map(n => ({ ...n, is_read: true }))
          );
          this.focusManagementService.focusNewElement('#notifications-heading', 'Focused moved to notifications list');
        }
      },
      error: (err: any) => {
        console.error('Error marking all notifications as read:', err);
      }
    });
  }

  /**
   * Navigate based on notification type
   * REVIEW_SUBMITTED: Call startReview API only for PENDING/NEEDS_REVISION, then navigate to admin review page
   * REVIEW_CHANGES_REQUESTED: Navigate to trainer edit page with review ID
   * Other types: Can be extended as needed
   */
  handleNotificationClick(notification: Notification & { metadata?: any }) {
    // Mark as read
    this.markNotificationAsRead(notification);

    // Navigate based on notification type
    if (notification.notification_type === 'REVIEW_SUBMITTED') {
      // Admin notification - handle based on review status
      const { course_id, review_id } = notification.metadata;
      if (course_id && review_id) {
        // Always fetch current course status to avoid calling startReview with stale metadata
        // Notification metadata can be outdated (e.g., notification created when PENDING, but now APPROVED)
        this.isProcessingNotification.set(true);
        this.courseFacade.getCourseForEdit(course_id)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (courseData: any) => {
              const currentReviewStatus = courseData?.review_status;

              // Only call startReview API if current status is PENDING or NEEDS_REVISION
              if (currentReviewStatus === 'PENDING' || currentReviewStatus === 'NEEDS_REVISION') {
                this.courseFacade.startReview(course_id, review_id)
                  .pipe(take(1),takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: (startReviewResponse: any) => {
                      this.isProcessingNotification.set(false);
                      if (startReviewResponse.success) {
                        // Navigate to review page after successful startReview
                        this.router.navigate(['/admin/courses', course_id, 'review'], {
                          queryParams: { reviewId: review_id }
                        });
                      } else {
                        this.messageService.add({
                          severity: 'warn',
                          summary: 'Warning',
                          detail: startReviewResponse.message || 'Failed to start review'
                        });
                      }
                    },
                    error: (error: any) => {
                      this.isProcessingNotification.set(false);
                      console.error('Error starting review from notification:', error);
                      this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to start review'
                      });
                    }
                  });
              } else {
                // For APPROVED, IN_PROGRESS, CHANGES_REQUESTED, or other statuses, just navigate
                this.isProcessingNotification.set(false);
                this.router.navigate(['/admin/courses', course_id, 'review'], {
                  queryParams: { reviewId: review_id }
                });
              }
            },
            error: (error: any) => {
              this.isProcessingNotification.set(false);
              console.error('Error fetching course status from notification:', error);
              // Still navigate even if fetch fails - review page will show error
              this.router.navigate(['/admin/courses', course_id, 'review'], {
                queryParams: { reviewId: review_id }
              });
            }
          });
      }
    } else if (notification.notification_type === 'REVIEW_CHANGES_REQUESTED') {
      // Trainer notification - navigate to trainer edit page with review ID
      const { course_id, review_id } = notification.metadata;
      if (course_id && review_id) {
        this.router.navigate(['/trainer/courses/edit', course_id], {
          queryParams: { reviewId: review_id }
        });
      }
    }
    // Add more notification types here as needed

    // Emit event to close the notification popover
    this.notificationClicked.emit();
  }

  /**
   * Toggle sound mute state
   */
  toggleSoundMute(): void {
    this.isSoundMuted.update(muted => !muted);
  }

  /**
   * Play notification sound from assets
   */
  private playNotificationSound(): void {
    // Don't play if sound is muted or during filter toggle
    if (this.isSoundMuted() || this.isTogglingFilter) {
      return;
    }

    try {
      const audio = this.notificationSound?.nativeElement;
      if (!audio) {
        console.warn('Audio element not found');
        return;
      }

      // Reset to start before playing
      audio.currentTime = 0;

      // Play the audio
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Notification sound played successfully');
          })
          .catch(err => {
            console.warn('Failed to play notification sound:', err.message || err);
          });
      }
    } catch (error) {
      console.warn('Error playing notification sound:', error);
    }
  }

  ngOnDestroy() {
    globalThis.window.removeEventListener('click', this.handleClickOutside);
    if(this.scrollToTopTimeOut) {
      clearTimeout(this.scrollToTopTimeOut);
      this.scrollToTopTimeOut = null;
    }
  }
}
